import { supabase } from './supabase';
import type { Database } from '../types/database';

export type WorkItemType = Database['public']['Enums']['work_item_type'];

export type PipelineKey = WorkItemType | null;

const SHARED_FALLBACK_STAGES: ReadonlyArray<string> = [
  'Backlog',
  'In progress',
  'Review',
  'Done'
];

const EPIC_FALLBACK_STAGES: ReadonlyArray<string> = [
  'Proposed',
  'Active',
  'Shipped',
  'Archived'
];

const SUBTASK_FALLBACK_STAGES: ReadonlyArray<string> = ['To do', 'Done'];

export function fallbackStagesFor(
  key: PipelineKey
): ReadonlyArray<string> {
  if (key === 'epic') return EPIC_FALLBACK_STAGES;
  if (key === 'subtask') return SUBTASK_FALLBACK_STAGES;
  return SHARED_FALLBACK_STAGES;
}

/**
 * One-shot fetch of stages for one or more pipeline keys. Pass `null` for the
 * shared pipeline (Backlog/In progress/Review/Done). Returns a Map keyed by
 * the `applies_to_type` enum value (or `null` for shared); callers fall back
 * to the hard-coded defaults via `fallbackStagesFor` when a key is missing.
 *
 * Always scoped to the active org. RLS is membership-based (it allows every
 * org the user belongs to), so the explicit `org_id` filter is required to
 * avoid pulling another org's pipeline rows for a multi-org user.
 */
export async function fetchTypePipelines(
  keys: ReadonlyArray<PipelineKey>,
  orgId: string
): Promise<{
  byType: Map<PipelineKey, ReadonlyArray<string>>;
  error: string | null;
}> {
  if (keys.length === 0) return { byType: new Map(), error: null };
  const orParts = keys.map((k) =>
    k === null ? 'applies_to_type.is.null' : `applies_to_type.eq.${k}`
  );
  const { data, error } = await supabase
    .from('work_item_status_pipelines')
    .select('applies_to_type, stages')
    .eq('org_id', orgId)
    .or(orParts.join(','));
  if (error) return { byType: new Map(), error: error.message };
  const map = new Map<PipelineKey, ReadonlyArray<string>>();
  for (const row of (data ?? []) as Array<{
    applies_to_type: PipelineKey;
    stages: unknown;
  }>) {
    if (map.has(row.applies_to_type)) continue;
    if (Array.isArray(row.stages) && row.stages.length > 0) {
      map.set(
        row.applies_to_type,
        (row.stages as ReadonlyArray<unknown>).map(String)
      );
    }
  }
  return { byType: map, error: null };
}

export function stagesFor(
  byType: Map<PipelineKey, ReadonlyArray<string>>,
  key: PipelineKey
): ReadonlyArray<string> {
  const found = byType.get(key);
  if (found && found.length > 0) return found;
  return fallbackStagesFor(key);
}
